import {
  PROJECT_SHARED_DOCUMENTS_MAX,
  ProjectFileUsage,
  SHARED_DOCUMENTS_ROLES,
  resolveDocumentAccess,
  sharedDocumentSide,
  sharedDocumentsOpenOutsideOffice,
  type SharedDocumentSide,
} from '@autoriz/shared/files'
import { AccountType } from '@autoriz/shared/accounts'
import type { FileView } from '@features/documents/types/documents.types'
import type { ProjectFileUsage as ProjectFileUsageValue } from 'src/utils/enums'
import { deleteFile } from 'src/services/gql/files'
import type { GqlClient } from '@/types/gqlClient'

/**
 * #883 — The shared documents of a dossier.
 *
 * An exchange space between the architect's office, the commercial side
 * (salesman or company) and the dossier's client: a photo taken afterwards, a
 * quote, a letter from the town hall. Always open — the office at every step,
 * the others at every step but an abandoned dossier. Never a dossier piece.
 *
 * This module holds the DECISIONS — who sees the section, who may add, who may
 * remove what — so the documents pages, the triage picker and the synthesis link
 * cannot disagree. The backend and Hasura enforce every one of them again; this
 * is what the screen offers.
 */

export const SHARED_DOCUMENT_USAGE = ProjectFileUsage.PROJECT_SHARED_DOCUMENT
export const SHARED_DOCUMENTS_TITLE = 'Documents partagés'
/** The section's anchor on the documents page, the target of every link to it. */
export const SHARED_DOCUMENTS_ANCHOR = 'documents-partages'

/** Whether the viewer has a section at all: the office, the commercial side, the client. */
export const hasSharedDocuments = (accountType: string | null | undefined): boolean =>
  !!accountType && SHARED_DOCUMENTS_ROLES.includes(accountType)

export const sharedDocumentsUrl = (projectId: number): string =>
  `/projects/edit/${projectId}/documents#${SHARED_DOCUMENTS_ANCHOR}`

export type SharedDocument = {
  fileId: string
  name: string
  mimeType: string | null
  createdAt: string
  /** Added by the commercial side (salesman or company). */
  addedByCommercial: boolean
  /** Added by a client. Neither: by the office. */
  addedByClient: boolean
  size: number | null
  updatedAt: string
}

const OFFICE_ROLES: readonly string[] = [AccountType.ARCHITECT, AccountType.COLLABORATOR]
const COMMERCIAL_ROLES: readonly string[] = [AccountType.SALESMAN, AccountType.SOCIETY]

/** Whether the viewer may put a file among the shared documents at this step. */
export const sharedDocumentsOffered = (
  accountType: string | null | undefined,
  step: string | null | undefined,
): boolean =>
  !!accountType && !!step && resolveDocumentAccess(SHARED_DOCUMENT_USAGE, { accountType, step }).editable

export type SharedDocumentRow = SharedDocument & {
  /** « Agence », « Commercial » or « Client », with the date, under the name. */
  author: string
  /** The viewer may remove it: the office any, the others what their side added. */
  removable: boolean
}

export type SharedDocumentsView = {
  documents: SharedDocumentRow[]
  /** Room for one more: the office always, the others while open and under the limit. */
  canAdd: boolean
  /** The room of the commercial side and the client, under the list; `null` for the office. */
  limit: number | null
  /** The office may move a shared document to a real usage (e.g. a photo slot). */
  canReclassify: boolean
  /** Why the viewer can no longer add, when it cannot. */
  closedReason: string | null
}

const AUTHOR_LABEL: Record<SharedDocumentSide, string> = {
  office: 'Agence',
  commercial: 'Commercial',
  client: 'Client',
}

/**
 * The section as the viewer sees it, or `null` when they see none: an unknown
 * role, or a commercial or a client on an abandoned dossier with nothing in it.
 */
export const sharedDocumentsView = (
  accountType: string | null | undefined,
  step: string | null | undefined,
  documents: readonly SharedDocument[],
): SharedDocumentsView | null => {
  if (!accountType || !step) return null
  if (!resolveDocumentAccess(SHARED_DOCUMENT_USAGE, { accountType, step }).visible) return null
  const office = OFFICE_ROLES.includes(accountType)
  const commercial = COMMERCIAL_ROLES.includes(accountType)
  const client = accountType === AccountType.CLIENT
  if (!office && !commercial && !client) return null
  const open = office || sharedDocumentsOpenOutsideOffice(step)
  if (!open && documents.length === 0) return null
  const full = !office && documents.length >= PROJECT_SHARED_DOCUMENTS_MAX
  return {
    documents: documents.map((document) => ({
      ...document,
      author: AUTHOR_LABEL[sharedDocumentSide(document.addedByCommercial, document.addedByClient)],
      removable: office || (open && (commercial ? document.addedByCommercial : document.addedByClient)),
    })),
    canAdd: open && !full,
    limit: office ? null : PROJECT_SHARED_DOCUMENTS_MAX,
    canReclassify: office,
    closedReason: !open
      ? 'Le dossier est abandonné : il ne prend plus de documents.'
      : full
        ? `${PROJECT_SHARED_DOCUMENTS_MAX} documents au maximum.`
        : null,
  }
}

const normalized = (text: string): string =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

/** Whether the section stays on the page for this search: its title, or a document's name. */
export const sharedDocumentsAnswerSearch = (
  documents: readonly { name: string }[],
  search: string,
): boolean => {
  const needle = normalized(search)
  return (
    !needle ||
    normalized(SHARED_DOCUMENTS_TITLE).includes(needle) ||
    documents.some((document) => normalized(document.name).includes(needle))
  )
}

/** A shared document as the documents page lists a file. */
export const sharedDocumentFileView = (document: SharedDocument): FileView => ({
  id: document.fileId,
  name: document.name,
  mime_type: document.mimeType ?? '',
  size_bytes: document.size,
  modified_at: document.updatedAt,
  created_at: document.createdAt,
  status: null,
  usage: SHARED_DOCUMENT_USAGE as ProjectFileUsageValue,
  origin: 'project',
})

type SharedDocumentsRow = {
  project_files: {
    file_id: string
    added_by_commercial: boolean | null
    added_by_client: boolean | null
    file: { id: string; name: string; mime_type: string | null; created_at: string; updated_at: string; size: number | null } | null
  }[]
}

/** The enabled shared documents of a dossier, oldest first. */
export const fetchProjectSharedDocuments = async (
  gqlClient: GqlClient,
  projectId: number,
): Promise<SharedDocument[]> => {
  const query = `
    query ProjectSharedDocuments($id: Int!) {
      project_files(
        where: {
          project_id: { _eq: $id }
          use: { _eq: PROJECT_SHARED_DOCUMENT }
          file: { enabled: { _eq: true } }
        }
        order_by: { file: { created_at: asc } }
      ) {
        file_id
        added_by_commercial
        added_by_client
        file {
          id
          name
          mime_type
          created_at
          updated_at
          size
        }
      }
    }
  `
  const result = (await gqlClient.request(query, { id: projectId })) as SharedDocumentsRow
  return result.project_files.flatMap((row) =>
    row.file
      ? [
          {
            fileId: row.file.id,
            name: row.file.name,
            mimeType: row.file.mime_type,
            createdAt: row.file.created_at,
            addedByCommercial: row.added_by_commercial === true,
            addedByClient: row.added_by_client === true,
            updatedAt: row.file.updated_at,
            size: row.file.size,
          },
        ]
      : [],
  )
}

/**
 * Removes a shared document. The office soft-deletes it like any document (its
 * « Supprimer »); the commercial side and the client delete the link, which
 * Hasura allows only on what their side added, on a dossier not abandoned — a
 * refusal comes back as `false`, and the caller says so.
 */
export const removeSharedDocument = async (
  gqlClient: GqlClient,
  projectId: number,
  fileId: string,
  side: 'office' | 'outside',
): Promise<boolean> => {
  if (side === 'office') {
    await deleteFile(gqlClient, fileId)
    return true
  }
  const mutation = `
    mutation RemoveProjectSharedDocument($projectId: Int!, $fileId: uuid!) {
      delete_project_files_by_pk(project_id: $projectId, file_id: $fileId) {
        file_id
      }
    }
  `
  const result = (await gqlClient.request(mutation, { projectId, fileId })) as {
    delete_project_files_by_pk: { file_id: string } | null
  }
  return result.delete_project_files_by_pk !== null
}

/** The office moves a shared document to a real usage (e.g. a photo slot). */
export const reclassifySharedDocument = async (
  gqlClient: GqlClient,
  projectId: number,
  fileId: string,
  usage: string,
): Promise<void> => {
  const mutation = `
    mutation ReclassifySharedDocument($projectId: Int!, $fileId: uuid!, $usage: project_files_uses_enum!) {
      update_project_files_by_pk(
        pk_columns: { project_id: $projectId, file_id: $fileId }
        _set: { use: $usage }
      ) {
        file_id
      }
    }
  `
  await gqlClient.request(mutation, { projectId, fileId, usage })
}
